import { useEffect, useRef, useState, useCallback } from "react"
import "../../lib/pdfjs-iterator-polyfill"
import * as pdfjsLib from "pdfjs-dist"
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url"

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

/**
 * Visor de PDF embebido en el propio DOM (render a <canvas> vía pdf.js), no
 * un <iframe>/<object> del visor nativo del navegador — eso corre en un
 * contexto aislado que no expone eventos de scroll al padre. Sin anotaciones,
 * búsqueda ni thumbnails: solo lo mínimo para poder medir avance de lectura.
 * Ver openspec/changes/academy-lesson-view-validation/design.md (decisión D3).
 */
export default function PdfViewer({ url, onScrollPercentChange }: {
  url: string
  onScrollPercentChange?: (percent: number) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pageCount, setPageCount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const container = containerRef.current
    if (!container) return

    setError(null)
    setPageCount(null)
    container.innerHTML = ""

    pdfjsLib.getDocument({ url }).promise
      .then(async (pdf) => {
        if (cancelled) return
        setPageCount(pdf.numPages)
        const containerWidth = container.clientWidth || 800

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          if (cancelled) return
          const page = await pdf.getPage(pageNumber)
          const unscaledViewport = page.getViewport({ scale: 1 })
          const scale = containerWidth / unscaledViewport.width
          const viewport = page.getViewport({ scale })

          const canvas = document.createElement("canvas")
          canvas.width = viewport.width
          canvas.height = viewport.height
          canvas.className = "mx-auto block w-full rounded-lg border border-border mb-4"
          const context = canvas.getContext("2d")
          if (!context) continue
          container.appendChild(canvas)
          await page.render({ canvas, canvasContext: context, viewport }).promise
        }
      })
      .catch(() => {
        if (!cancelled) setError("No pudimos cargar el documento. Intenta abrirlo desde el enlace de descarga.")
      })

    return () => {
      cancelled = true
    }
  }, [url])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollPercentChange) return
    const el = e.currentTarget
    const scrollable = el.scrollHeight - el.clientHeight
    const percent = scrollable > 0 ? Math.min(100, Math.max(0, (el.scrollTop / scrollable) * 100)) : 100
    onScrollPercentChange(percent)
  }, [onScrollPercentChange])

  if (error) {
    return (
      <div className="flex aspect-video items-center justify-center bg-gradient-to-br from-slate-900 to-black p-12 text-center text-foreground">
        <p className="text-muted-foreground">{error}</p>
      </div>
    )
  }

  return (
    <div className="relative bg-slate-950">
      {pageCount === null && (
        <div role="status" className="flex aspect-video items-center justify-center text-muted-foreground">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" aria-hidden="true" />
          <span className="sr-only">Cargando documento…</span>
        </div>
      )}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="max-h-[80vh] overflow-y-auto scroll-thin p-4"
      />
    </div>
  )
}
